// frontend/src/pages/Circle.js
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Preferences } from "@capacitor/preferences";
import { api } from "api";
import { Capacitor } from "@capacitor/core";
import { API_BASE_URL } from "../api";
import AppHeader from "../components/AppHeader";
import CircleSlimBanner from "components/CircleSlimBanner";
import ActivityUpload from "../pages/ActivityUpload";
import ActivityCard from "../components/ActivityCard";
import AvatarStack from "../components/AvatarStack";
import CircleToast from "../components/CircleToast";
import EditCircleDetailsModal from "components/EditCircleDetailsModal";
import MembersModal from "../components/MembersModal";
import MonthlyCalendarModal from "../components/MonthlyCalendarModal";
import WeeklyCalendarModal from "../components/WeeklyCalendarModal";
import WeeklyPlanModal from "../components/WeeklyPlanModal";
import GoalsNote from "../components/GoalsNote";
import CircleAdminModal from "../components/CircleAdminModal";
import { isIOS, isMobile } from "../utils/platform";
import RemindersModal from "../components/RemindersModal";
import GoalsByMonth from "../components/GoalsByMonth";
import PlannerQuickPanel from "../components/PlannerQuickPanel";
import { DEFAULT_CIRCLE_IMAGE, DEFAULT_PROFILE_IMAGE } from "../utils/blank-image";
import { computeCalendarMarks } from "../utils/streaks";
import CropperModal from "../components/CropperModal";
import { PencilSimpleLine, UploadSimple } from "phosphor-react";
import EventCreateModal from "../components/EventCreateModal";
import EventsListModal from "../components/EventsListModal";
import UpcomingEventCard from "../components/UpcomingEventCard";
import RsvpListModal from "../components/RsvpListModal";
import AnnouncementsBanner from "../components/AnnouncementsBanner";
import AnnouncementCreateModal from "../components/AnnouncementCreateModal";
import AnnouncementsListModal from "../components/AnnouncementsListModal";
import { COUNTRY_OPTIONS } from "../utils/countries";

import {
  CheckCircle,
  XCircle,
  CopySimple,
  LockSimple,
  LockSimpleOpen,
  PushPin,
  TrashSimple,
  CalendarBlank,
  LinkSimple,
  PencilSimple,
  X,
} from "phosphor-react";

const STICKY_TOP = "calc(env(safe-area-inset-top, 0px) + 64px)";
const RAIL_MAX_HEIGHT = `calc(100dvh - ${STICKY_TOP} - 16px)`;
const API_BASE = API_BASE_URL;

const toAbsolute = (url) => {
  if (!url) return null;
  if (
    url.startsWith("http://") ||
    url.startsWith("https://") ||
    url.startsWith("data:") ||
    url.startsWith("blob:")
  )
    return url;
  return `${API_BASE}${url.startsWith("/") ? "" : "/"}${url}`;
};

const toAvatar = (u) =>
  toAbsolute(
    u?.profile_pic_url ??
      u?.avatar_url ??
      u?.profile_image_url ??
      u?.image_url ??
      u?.photo_url ??
      u?.picture_url ??
      u?.user?.profile_pic_url ??
      u?.user?.avatar_url ??
      u?.user?.profile_image_url ??
      u?.user?.image_url ??
      null
  ) || DEFAULT_PROFILE_IMAGE;

const codeToFlag = (code) => {
  const cc = String(code || "").trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return null;
  const base = 0x1f1e6;
  const A = "A".charCodeAt(0);
  return String.fromCodePoint(base + (cc.charCodeAt(0) - A), base + (cc.charCodeAt(1) - A));
};
const coalesce = (...vals) => vals.find((v) => v !== undefined && v !== null && String(v).trim() !== "");
const circleLogoUrl = (circle) =>
  toAbsolute(
    coalesce(
      circle?.logo_url,
      circle?.image_url,
      circle?.picture_url,
      circle?.avatar_url,
      circle?.icon_url,
      circle?.banner_url
    )
  ) || DEFAULT_CIRCLE_IMAGE;

const circleWebsite = (circle) =>
  coalesce(circle?.website, circle?.website_url, circle?.url, circle?.homepage, circle?.link);

const circleLocation = (circle) =>
  coalesce(
    circle?.location,
    [circle?.city, circle?.state || circle?.region, circle?.country].filter(Boolean).join(", ")
  );

const circleCountryCode = (circle) =>
  coalesce(
    circle?.country_code,
    typeof circle?.country === "string" && circle.country.length === 2 ? circle.country : null,
    /new york/i.test(circleLocation(circle) || "") ? "US" : null
  );

const groupByDay = (list = []) => {
  const map = {};
  list.forEach((a) => {
    const key = ymd(a.timestamp || a.created_at);
    (map[key] ||= []).push(a);
  });
  Object.keys(map).forEach((k) => {
    map[k].sort(
      (a, b) =>
        new Date(a.timestamp || a.created_at) -
        new Date(b.timestamp || b.created_at)
    );
  });
  return map;
};

async function getToken() {
  try {
    const pref = await Preferences.get({ key: "auth_token" });
    if (pref && pref.value) return pref.value;
  } catch {}
  return (
    localStorage.getItem("token") ||
    sessionStorage.getItem("token") ||
    null
  );
}

async function getAuthHeaders() {
  const token = await getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const getMemberId = (member) =>
  member?.id ??
  member?.user_id ??
  member?.member_id ??
  member?.user?.id ??
  member?.member?.id ??
  null;

const normalizeMember = (member) => {
  const nestedUser = member?.user || member?.member || {};

  return {
    ...member,
    ...nestedUser,

    id:
      nestedUser?.id ??
      member?.id ??
      member?.user_id ??
      member?.member_id ??
      null,

    name:
      nestedUser?.name ??
      nestedUser?.display_name ??
      nestedUser?.username ??
      member?.name ??
      member?.display_name ??
      member?.username ??
      null,

    username:
      nestedUser?.username ??
      member?.username ??
      null,

    profile_pic_url:
      nestedUser?.profile_pic_url ??
      member?.profile_pic_url ??
      null,
  };
};

const hydrateMembers = async (rawMembers = [], headers = {}) => {
  const rows = Array.isArray(rawMembers) ? rawMembers : [];

  return Promise.all(
    rows.map(async (rawMember) => {
      const normalized = normalizeMember(rawMember);
      const memberId = getMemberId(normalized);

      if (!memberId) return normalized;

      try {
        const { data } = await api.get(`/users/${memberId}`, {
          headers,
        });

        const fullUser = data || {};

        return {
          ...normalized,
          ...fullUser,

          id:
            fullUser?.id ??
            normalized?.id ??
            memberId,

          name:
            fullUser?.name ??
            fullUser?.display_name ??
            fullUser?.username ??
            normalized?.name ??
            `User #${memberId}`,

          username:
            fullUser?.username ??
            normalized?.username ??
            null,

          profile_pic_url:
            fullUser?.profile_pic_url ??
            fullUser?.avatar_url ??
            fullUser?.profile_image_url ??
            fullUser?.image_url ??
            normalized?.profile_pic_url ??
            null,
        };
      } catch {
        return normalized;
      }
    })
  );
};

async function copyText(text) {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {}
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

async function getCroppedImage(srcUrl, cropAreaPixels, mime = "image/png", quality = 0.92) {
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = srcUrl;
  });
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.floor(cropAreaPixels.width));
  canvas.height = Math.max(1, Math.floor(cropAreaPixels.height));
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(
    img,
    cropAreaPixels.x,
    cropAreaPixels.y,
    cropAreaPixels.width,
    cropAreaPixels.height,
    0,
    0,
    canvas.width,
    canvas.height
  );
  const blob = await new Promise((res) => canvas.toBlob(res, mime, quality));
  return blob;
}

const normalizeActivity = (a, lookup = {}) => {
  const uid =
    a?.user?.id ?? a?.author?.id ?? a?.user_id ?? a?.author_id ?? null;

  const fromLookup = uid ? lookup[uid] : null;

  const name =
    a?.user?.name ??
    a?.author?.name ??
    a?.user_name ??
    a?.author_name ??
    a?.username ??
    a?.name ??
    fromLookup?.name ??
    "Unknown user";

  const profile_pic_url =
    a?.user?.profile_pic_url ??
    a?.author?.profile_pic_url ??
    a?.user_profile_pic_url ??
    a?.author_profile_pic_url ??
    a?.profile_pic_url ??
    fromLookup?.profile_pic_url ??
    null;

  const user = uid ? { id: uid, name, profile_pic_url } : null;

  return {
    ...a,
    user,
    tags:
      Array.isArray(a.tags) && a.tags.length
        ? a.tags
        : typeof a.tags === "string"
        ? a
            .tags
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean)
        : [],
  };
};

const ymd = (d) => {
  const dt = new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const fmtEventDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
};
const fmtEventTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
};

const dayTile = (iso) => {
  if (!iso) return { d: "", m: "" };
  const d = new Date(iso);
  const day = String(d.getDate());
  const mon = d.toLocaleString("en-US", { month: "short" }).toUpperCase();
  return { d: day, m: mon };
};

const RSVP_WORD = "I’m in";

const rsvp = async (eventId, going, getHeaders) => {
  try {
    const headers = await getHeaders();
    await api.post(`/groups/events/${eventId}/rsvp?going=${going ? "true" : "false"}`, {}, { headers });
    return true;
  } catch {
    return false;
  }
};

const Circle = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { id } = useParams();
  const [user, setUser] = useState(null);
  const onIOS = !!isIOS;

  const compactBtn =
    "overflow-hidden leading-none [min-width:0] [min-height:0]";

  const pillBtn = `${compactBtn} rounded-full`;

  const mdBtn = `${compactBtn} rounded-md`;

  const [me, setMe] = useState(null);
  const [circle, setCircle] = useState(null);
  const [members, setMembers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [isJoined, setIsJoined] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const [activities, setActivities] = useState([]);
  const [myActivities, setMyActivities] = useState([]);
  const [selectedTag, setSelectedTag] = useState(null);

  const [showMembers, setShowMembers] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false);

  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [rsvpEventId, setRsvpEventId] = useState(null);

  const [showMonthly, setShowMonthly] = useState(false);
  const [showWeekly, setShowWeekly] = useState(false);
  const [showWeekPlan, setShowWeekPlan] = useState(false);
  const [showReminders, setShowReminders] = useState(false);
  const [showGoalsByMonth, setGoalsByMonth] = useState(false);

  const [editingTitle, setEditingTitle] = useState(false);
  const [editingSubtitle, setEditingSubtitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [subtitleDraft, setSubtitleDraft] = useState("");
  const [savingMeta, setSavingMeta] = useState(false);
  const [showEditMeta, setShowEditMeta] = useState(false);
  const [draftNameAll, setDraftNameAll] = useState("");
  const [draftLocationAll, setDraftLocationAll] = useState("");
  const [draftWebsiteAll, setDraftWebsiteAll] = useState("");
  const [draftDescriptionAll, setDraftDescriptionAll] = useState("");
  const [draftCityAll, setDraftCityAll] = useState("");
  const [draftCountryCodeAll, setDraftCountryCodeAll] = useState("");

  const [latestAnnouncement, setLatestAnnouncement] = useState(null);
  const [allAnnouncements, setAllAnnouncements] = useState([]);
  const [showCreateAnnouncement, setShowCreateAnnouncement] = useState(false);
  const [showAnnouncementsList, setShowAnnouncementsList] = useState(false);

  const [confirmingDeleteId, setConfirmingDeleteId] = useState(null);
  const [deletingActivityId, setDeletingActivityId] = useState(null);

  const [logoBuster, setLogoBuster] = useState(0);

  const [isNarrowView, setIsNarrowView] = React.useState(() => {
    try {
      return typeof window !== "undefined"
        ? window.matchMedia("(max-width: 767px)").matches
        : false;
    } catch {
      return false;
    }
  });

  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = (e) => setIsNarrowView(e.matches);
    try {
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    } catch {
      mq.addListener(onChange);
      return () => mq.removeListener(onChange);
    }
  }, []);

  const mobileView = onIOS || isMobile || isNarrowView;

  const countryDisplayName = useMemo(() => {
    try {
      return draftCountryCodeAll
        ? new Intl.DisplayNames(["en"], { type: "region" }).of(draftCountryCodeAll)
        : "";
    } catch {
      return "";
    }
  }, [draftCountryCodeAll]);
  
  const draftLocationLabel = useMemo(() => {
    if (draftCityAll && countryDisplayName) return `${draftCityAll}, ${countryDisplayName}`;
    if (countryDisplayName) return countryDisplayName;
    return draftCityAll || "";
  }, [draftCityAll, countryDisplayName]);

  const countryOptions = useMemo(() => {
    try {
      const regions =
        (typeof Intl?.supportedValuesOf === "function" &&
          Intl.supportedValuesOf("region")) ||
        ["US", "CA", "GB", "KR", "JP", "DE", "FR", "IT", "ES", "AU", "SG"];
      const dn = new Intl.DisplayNames(["en"], { type: "region" });
      return regions
        .map((code) => {
          const name = dn.of(code);
          return name ? { code, name } : null;
        })
        .filter(Boolean)
        .sort((a, b) => a.name.localeCompare(b.name));
    } catch {
      return [
        { code: "US", name: "United States" },
        { code: "CA", name: "Canada" },
        { code: "GB", name: "United Kingdom" },
        { code: "KR", name: "South Korea" },
        { code: "JP", name: "Japan" },
      ];
    }
  }, []);
  
  const logoFileInputRef = useRef(null);

  const [logoCropSrc, setLogoCropSrc] = useState(null);
  const [showLogoCropper, setShowLogoCropper] = useState(false);
  const [logoCrop, setLogoCrop] = useState({ x: 0, y: 0 });
  const [logoZoom, setLogoZoom] = useState(1);
  const [logoCropPixels, setLogoCropPixels] = useState(null);
  const [logoAspect, setLogoAspect] = useState(1);

  const onLogoCropComplete = useCallback((_area, areaPixels) => {
    setLogoCropPixels(areaPixels);
  }, []);

  const [bannerPreview, setBannerPreview] = useState(null);
  const bannerInputRef = useRef(null);

  const [uploadOpen, setUploadOpen] = useState(false);

  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);
  const showToast = (message, kind = "success", ms = 2400, copyAgain = false) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ message, kind, copyAgain });
    toastTimerRef.current = setTimeout(() => setToast(null), ms);
  };
  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  /* ---- Events state ---- */
  const [upcomingEvent, setUpcomingEvent] = useState(null);
  const [allUpcomingEvents, setAllUpcomingEvents] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [showAllEvents, setShowAllEvents] = useState(false);
  const [highlightEventId, setHighlightEventId] = useState(null);

  const openEventsListForEvent = React.useCallback(
    (eventId) => {
      setHighlightEventId(eventId);

      if (mobileView) {
        navigate(`/circles/${id}/events/list?event=${eventId}`);
        return;
      }

      setShowAllEvents(true);
    },
    [mobileView, navigate, id]
  );

  const meId = React.useMemo(() => {
    if (typeof me?.id === "number" && Number.isFinite(me.id)) return me.id;
    const ls = Number(localStorage.getItem("user_id"));
    return Number.isFinite(ls) ? ls : null;
  }, [me]);

  const firstFour = useMemo(() => members.slice(0, 4), [members]);

  const myActivitiesByDay = useMemo(() => groupByDay(myActivities), [myActivities]);

  const streakDays = useMemo(() => {
    return computeCalendarMarks({
      activityDates: Object.keys(myActivitiesByDay || {}),
    }).streakCount;
  }, [myActivitiesByDay]);

  const monthLabel = useMemo(
    () =>
      new Date()
        .toLocaleString("en-US", { month: "short" })
        .slice(0, 3)
        .toUpperCase(),
    []
  );

  // Pick the next event from a sorted list (with a small grace period)
  const pickNextFrom = (list = []) => {
    const now = Date.now();
    const graceMs = 5 * 60 * 1000;
    const idx = list.findIndex(ev => new Date(ev.start_time).getTime() >= now - graceMs);
    return idx >= 0 ? list[idx] : list[0] || null;
  };  

  // Request guard to prevent race conditions
  const eventsReqSeq = React.useRef(0);

  const handleUploadClick = () => {
    if (mobileView) {
      navigate("/uploadactivity", {
        state: {
          circleId: circle?.id ?? null,
          circleName: circle?.name ?? null,
          redirectTo: circle?.id
            ? { type: "circle", id: circle.id }
            : null,
          canGoBack: true,
        },
      });

      return;
    }

    setUploadOpen(true);
  };

  const openLogoPicker = () => {
    if (!isAdmin) return;
    logoFileInputRef.current?.click();
  };
  
  const onLogoFileChosen = (e) => {
    if (!isAdmin) return;
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const url = URL.createObjectURL(file);
      setLogoCropSrc(url);
      setLogoZoom(1);
      setLogoCrop({ x: 0, y: 0 });
      setLogoCropPixels(null);
      setShowLogoCropper(true);
    } catch {
      showToast("Could not open image", "error");
    }
  };
  
  const uploadLogoFile = async (file) => {
    const headers = await getAuthHeaders();
    const form = new FormData();
    form.append("file", file);
    const endpoints = [
      `/groups/${id}/logo`,
      `/groups/${id}/image`,
      `/groups/${id}/picture`,
    ];
    for (const path of endpoints) {
      try {
        const { data } = await api.post(path, form, {
          headers: { ...headers, "Content-Type": "multipart/form-data" },
        });
        const url = data?.logo_url || data?.image_url || data?.url || null;
        if (url) return url;
      } catch {}
    }
    return null;
  };
  
  const applyCroppedLogo = async () => {
    if (!logoCropSrc || !logoCropPixels) return;
    try {
      const blob = await getCroppedImage(logoCropSrc, logoCropPixels, "image/png", 0.92);
      const file = new File([blob], "logo.png", { type: "image/png" });
      const uploaded = await uploadLogoFile(file);
      if (uploaded) {
        setCircle((c) => ({ ...c, logo_url: uploaded, image_url: uploaded }));
        setLogoBuster(Date.now());
        showToast("Logo updated", "success");
      } else {
        showToast("Upload failed", "error");
      }
    } finally {
      URL.revokeObjectURL(logoCropSrc);
      setLogoCropSrc(null);
      setShowLogoCropper(false);
      setLogoCropPixels(null);
    }
  };
  
  const skipLogoCrop = async () => {
    try {
      if (!logoCropSrc) {
        setShowLogoCropper(false);
        return;
      }
      const resp = await fetch(logoCropSrc);
      const blob = await resp.blob();
      const file = new File([blob], "logo.png", { type: blob.type || "image/png" });
      const uploaded = await uploadLogoFile(file);
      if (uploaded) {
        setCircle((c) => ({ ...c, logo_url: uploaded, image_url: uploaded }));
        setLogoBuster(Date.now());
        showToast("Logo updated", "success");
      } else {
        showToast("Upload failed", "error");
      }
    } catch {
      showToast("Upload failed", "error");
    } finally {
      URL.revokeObjectURL(logoCropSrc);
      setLogoCropSrc(null);
      setShowLogoCropper(false);
      setLogoCropPixels(null);
    }
  };
  
  const refreshGroup = async () => {
    try {
      const headers = await getAuthHeaders();
      const { data } = await api.get(`/groups/${id}`, { headers });
      setCircle(data);
    } catch (e) {
      console.error("Failed to refresh group:", e);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const headers = await getAuthHeaders();
        const [meRes, groupRes, memRes, adminRes] = await Promise.all([
          api.get("/users/me", { headers }).catch(() => ({ data: null })),
          api.get(`/groups/${id}`, { headers }),
          api.get(`/groups/${id}/members`, { headers }),
          api.get(`/groups/${id}/admins`, { headers }).catch(() => ({ data: [] })),
        ]);

        setMe(meRes?.data || null);
        setCircle(groupRes.data);
        setTitleDraft(groupRes?.data?.name || "");
        setSubtitleDraft(groupRes?.data?.description || "");
        setDraftNameAll(groupRes?.data?.name || "");
        setDraftLocationAll(
          circleLocation(groupRes.data) || groupRes?.data?.location || ""
        );
        setDraftWebsiteAll(circleWebsite(groupRes.data) || "");
        setDraftDescriptionAll(groupRes?.data?.description || "");
        const initLoc = circleLocation(groupRes.data) || groupRes?.data?.location || "";
        const ccInit = circleCountryCode(groupRes.data) || "";
        const cityInit = initLoc.split(",")[0]?.trim() || "";
        setDraftCityAll(cityInit);
        setDraftCountryCodeAll((ccInit || "").toUpperCase());

        const hydratedMembers = await hydrateMembers(
          memRes.data || [],
          headers
        );

        setMembers(hydratedMembers);
        setAdmins(
          Array.isArray(adminRes?.data) ? adminRes.data : []
        );

        if (meRes?.data?.id) {
          try {
            const mine = await api.get(`/users/${meRes.data.id}/activities`, { headers });
            setMyActivities(Array.isArray(mine.data) ? mine.data : []);
          } catch (err) {
            console.error("Failed to fetch my activities:", err);
            setMyActivities([]);
          }
        }

        const meStr = String(meRes?.data?.id || localStorage.getItem("user_id"));
        setIsJoined(
          hydratedMembers.some(
            (member) =>
              String(getMemberId(member)) === meStr
          )
        );
        setIsAdmin((adminRes?.data || []).some((a) => String(a.id) === meStr));
      } catch (e) {
        console.error("Circle bootstrap failed:", e);
      }
    })();
  }, [id]);

  /* ---- Events fetching ---- */
  const fetchAllEvents = React.useCallback(async () => {
    const mySeq = ++eventsReqSeq.current;

    try {
      const headers = await getAuthHeaders();

      const [futureRes, allRes] = await Promise.all([
        api.get(`/groups/${id}/events`, { headers }),
        api.get(`/groups/${id}/events?include_past=true&limit=200`, { headers }),
      ]);

      const futureRows = Array.isArray(futureRes?.data) ? futureRes.data : [];
      const allRows = Array.isArray(allRes?.data) ? allRes.data : [];

      const dedupe = (rows = []) => {
        const seen = new Set();
        return rows.filter((e) => {
          if (!e?.id || !e?.start_time) return false;
          if (seen.has(e.id)) return false;
          seen.add(e.id);
          return true;
        });
      };

      const future = dedupe(futureRows).sort(
        (a, b) => new Date(a.start_time) - new Date(b.start_time)
      );

      const everything = dedupe(allRows).sort(
        (a, b) => new Date(b.start_time) - new Date(a.start_time)
      );

      if (mySeq !== eventsReqSeq.current) return future;

      setAllUpcomingEvents(future);
      setAllEvents(everything);
      setUpcomingEvent(pickNextFrom(future));

      return future;
    } catch (e) {
      if (mySeq !== eventsReqSeq.current) return [];
      setAllUpcomingEvents([]);
      setAllEvents([]);
      setUpcomingEvent(null);
      return [];
    }
  }, [id]);

  useEffect(() => {
    fetchAllEvents();
  }, [fetchAllEvents]);

  const openEventsList = React.useCallback(() => {
    if (mobileView) {
      navigate(`/circles/${id}/events/list`);
      return;
    }

    setShowAllEvents(true);
  }, [mobileView, navigate, id]);

  const fetchAnnouncements = React.useCallback(async () => {
    try {
      const headers = await getAuthHeaders();
      const { data } = await api.get(`/groups/${id}/announcements`, { headers });
      const rows = Array.isArray(data) ? data : [];
      // sort newest first by created_at if present, else leave order
      rows.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
      setAllAnnouncements(rows);
      setLatestAnnouncement(rows[0] || null);
    } catch {
      setAllAnnouncements([]);
      setLatestAnnouncement(null);
    }
  }, [id]);
  
  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  const editAnnouncement = React.useCallback(
    async (annId, { title, body, pinned }) => {
      // store as single "body" on backend: first line = title, rest = body
      const combined =
        (title || "").trim()
          ? `${(title || "").trim()}\n\n${(body || "").trim()}`
          : (body || "").trim();

      const headers = await getAuthHeaders();
      await api.patch(`/groups/${id}/announcements/${annId}`, {
        body: combined,
        pinned: !!pinned,
      }, { headers });

      await fetchAnnouncements();
    },
    [id, fetchAnnouncements]
  );

  const deleteAnnouncement = React.useCallback(
    async (annId) => {
      const headers = await getAuthHeaders();
      await api.delete(`/groups/${id}/announcements/${annId}`, { headers });
      await fetchAnnouncements();
    },
    [id, fetchAnnouncements]
  );

  
  const fetchFeed = async (tag = null) => {
    try {
      const headers = await getAuthHeaders();
      const url = tag
        ? `/groups/${id}/feed?tag=${encodeURIComponent(tag)}`
        : `/groups/${id}/feed`;

      const { data } = await api.get(url, { headers });
      const raw = Array.isArray(data) ? data : [];

      const idsToHydrate = Array.from(
        new Set(
          raw
            .map((a) => {
              const uid =
                a?.user?.id ??
                a?.author?.id ??
                a?.user_id ??
                a?.author_id ??
                null;
              const hasName =
                a?.user?.name ||
                a?.author?.name ||
                a?.user_name ||
                a?.author_name ||
                a?.username ||
                a?.name;
              return !hasName ? uid : null;
            })
            .filter(Boolean)
        )
      );

      const usersById = {};
      if (idsToHydrate.length) {
        await Promise.all(
          idsToHydrate.map((uid) =>
            api
              .get(`/users/${uid}`, { headers })
              .then((r) => {
                const u = r?.data || {};
                usersById[uid] = {
                  id: u.id,
                  name: u.name || u.username || `User #${u.id}`,
                  profile_pic_url: u.profile_pic_url || u.avatar_url || null,
                };
              })
              .catch(() => {})
          )
        );
      }

      setActivities(raw.map((a) => normalizeActivity(a, usersById)));
    } catch (e) {
      console.error("Circle feed failed:", e);
      setActivities([]);
    }
  };

  useEffect(() => {
    fetchFeed(selectedTag);
  }, [id]); // eslint-disable-line

  const onTagClick = (tag) => {
    setSelectedTag(tag);
    fetchFeed(tag);
  };
  const clearTag = () => {
    setSelectedTag(null);
    fetchFeed(null);
  };

  const myActivityDates = React.useMemo(() => {
    if (meId == null) return [];
    return activities
      .filter((a) => ((a?.user?.id ?? a?.user_id) === meId))
      .map((a) => ymd(a.timestamp || a.created_at));
  }, [activities, meId]);

  const join = async () => {
    try {
      const headers = await getAuthHeaders();
      await api.post(`/groups/${id}/join/${meId}`, {}, { headers });
      setIsJoined(true);
      const [{ data: membersData }, { data: adminsData }] = await Promise.all([
        api.get(`/groups/${id}/members`, { headers }),
        api.get(`/groups/${id}/admins`, { headers }).catch(() => ({ data: [] })),
      ]);
      const hydratedMembers = await hydrateMembers(
        membersData || [],
        headers
      );

      setMembers(hydratedMembers);
      setAdmins(adminsData || []);
      setIsAdmin((adminsData || []).some((a) => String(a.id) === String(meId)));
      showToast("Joined circle", "success");
    } catch (e) {
      console.error("Join failed:", e);
      showToast("Failed to join circle", "error");
    }
  };

  const leave = async () => {
    try {
      const headers = await getAuthHeaders();
      await api
        .delete(`/groups/${id}/members/${meId}`, { headers })
        .catch(() => api.post(`/groups/${id}/leave/${meId}`, {}, { headers }));
      setIsJoined(false);
      const { data } = await api.get(
        `/groups/${id}/members`,
        { headers }
      );

      const hydratedMembers = await hydrateMembers(
        data || [],
        headers
      );

      setMembers(hydratedMembers);
      setIsAdmin(false);
      showToast("Left circle", "success");
    } catch (e) {
      console.error("Leave failed:", e);
      showToast("Failed to leave circle", "error");
    }
  };

  const invite = async () => {
    const link = window.location.href;
    const ok = await copyText(link);
    if (ok) {
      showToast("Invite link copied to clipboard", "success", 4400, true);
    } else {
      showToast("Copy failed — please copy from the address bar", "error");
    }
  };

  const onPickBanner = () => {
    if (!isAdmin) return;
    bannerInputRef.current?.click();
  };
  const tryUploadTo = async (form, headers, path) => {
    try {
      const { data } = await api.post(path, form, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      return data?.banner_url || data?.image_url || data?.url || null;
    } catch {
      return null;
    }
  };
  const onBannerChosen = async (e) => {
    if (!isAdmin) return;
    const file = e.target.files?.[0];
    if (!file) return;
    const localUrl = URL.createObjectURL(file);
    setBannerPreview(localUrl);
    const authHeaders = await getAuthHeaders();
    const form = new FormData();
    form.append("file", file);
    const endpoints = [`/groups/${id}/banner`, `/groups/${id}/image`, `/groups/${id}/picture`];
    let uploadedUrl = null;
    for (const p of endpoints) {
      const url = await tryUploadTo(form, authHeaders, p);
      if (url) {
        uploadedUrl = url;
        break;
      }
    }
    if (!uploadedUrl) showToast("Upload endpoint not found", "error");
    e.target.value = "";
  };

  const saveMeta = async (fields) => {
    if (!isAdmin) return;
    try {
      setSavingMeta(true);
      const headers = await getAuthHeaders();
      const { data } = await api.patch(`/groups/${id}`, fields, { headers });
      setCircle((c) => ({ ...c, ...data, ...fields }));
    } catch (e) {
      console.error("Could not save changes:", e);
      showToast("Could not save changes", "error");
    } finally {
      setSavingMeta(false);
    }
  };

  const deleteLogo = async () => {
    if (!isAdmin) return;

    try {
      const headers = await getAuthHeaders();

      await api.delete(`/groups/${id}/logo`, {
        headers,
      });

      setCircle((c) => ({
        ...c,
        logo_url: null,
        image_url: null,
      }));

      setLogoBuster(Date.now());
      showToast("Logo removed", "success");
    } catch (e) {
      console.error(e);
      showToast("Could not remove logo", "error");
    }
  };

  const saveAllMetaAndClose = async () => {
    if (!isAdmin) return;
    const payload = {};
    if (titleDraft !== circle.name) payload.name = titleDraft;
    if (subtitleDraft !== circle.description) payload.description = subtitleDraft;
    if (Object.keys(payload).length) await saveMeta(payload);
    setEditingTitle(false);
    setEditingSubtitle(false);
  };

  const openEditAllModal = () => {
    setDraftNameAll(circle?.name || "");
    const initLoc = circleLocation(circle) || circle?.location || "";
    setDraftLocationAll(initLoc);
    setDraftWebsiteAll(circleWebsite(circle) || "");
    setDraftDescriptionAll(circle?.description || "");
    const ccInit = circleCountryCode(circle) || "";
    const cityInit = initLoc.split(",")[0]?.trim() || "";
    setDraftCityAll(cityInit);
    setDraftCountryCodeAll((ccInit || "").toUpperCase());
    setShowEditMeta(true);
  };
  
  const closeEditAllModal = () => setShowEditMeta(false);

  const saveAllFields = async () => {
    if (!isAdmin) return;
    const payload = {};
  
    if ((draftNameAll || "") !== (circle?.name || "")) {
      payload.name = draftNameAll || "";
    }
  
    if ((draftDescriptionAll || "") !== (circle?.description || "")) {
      payload.description = draftDescriptionAll || "";
    }
  
    const currentLocLabel = circleLocation(circle) || circle?.location || "";
    const nextLocLabel = draftLocationLabel || "";
    if (nextLocLabel !== (currentLocLabel || "")) {
      payload.location = nextLocLabel;
      payload.location_label = nextLocLabel;
    }
  
    const currentCC = (circleCountryCode(circle) || "").toUpperCase();
    const nextCC = (draftCountryCodeAll || "").toUpperCase();
    if (nextCC && nextCC !== currentCC) {
      payload.country_code = nextCC;
    }
  
    const currentSite = circleWebsite(circle) || "";
    if ((draftWebsiteAll || "") !== currentSite) {
      payload.website = draftWebsiteAll || "";
    }
  
    if (Object.keys(payload).length === 0) {
      setShowEditMeta(false);
      return;
    }
  
    await saveMeta(payload);
    setShowEditMeta(false);
    showToast("Circle details updated", "success");
  };
  
  const onMetaKey = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      saveAllMetaAndClose();
    } else if (e.key === "Escape") {
      setTitleDraft(circle.name || "");
      setSubtitleDraft(circle.description || "");
      setEditingTitle(false);
      setEditingSubtitle(false);
    }
  };

  const togglePrivacy = async () => {
    if (!isAdmin) return;
    const next = !circle?.is_private;
    try {
      await saveMeta({ is_private: next });
    } catch {}
  };

  const pinnedId = circle?.pinned_activity_id || null;

  const pinActivity = async (activityId) => {
    if (!isAdmin || !activityId) return;
    try {
      const headers = await getAuthHeaders();
      await api.post(`/groups/${id}/pin/${activityId}`, {}, { headers });
      await refreshGroup();
    } catch (e) {
      console.error("Pin failed:", e);
      showToast("Failed to pin", "error");
    }
  };

  const unpinActivity = async () => {
    if (!isAdmin) return;
    try {
      const headers = await getAuthHeaders();
      await api.delete(`/groups/${id}/pin`, { headers });
      await refreshGroup();
      showToast("Unpinned", "success");
    } catch (e) {
      console.error("Unpin failed:", e);
      showToast("Failed to unpin", "error");
    }
  };

  const deleteActivity = async (activityId) => {
    if (!activityId) return;

    try {
      setDeletingActivityId(activityId);

      const headers = await getAuthHeaders();
      await api.delete(`/activities/${activityId}`, {
        headers,
        params: { circle_id: id },
      });

      await fetchFeed(selectedTag);
      if (pinnedId === activityId) await refreshGroup();

      setConfirmingDeleteId(null);
      showToast("Post deleted", "success");
    } catch (e) {
      console.error("Delete failed:", e);
      showToast("Failed to delete", "error");
    } finally {
      setDeletingActivityId(null);
    }
  };

  const sortedActivities = useMemo(() => {
    if (!activities?.length) return [];
    if (!pinnedId) return activities;
    const pinned = activities.find((a) => a.id === pinnedId);
    const rest = activities.filter((a) => a.id !== pinnedId);
    return pinned ? [pinned, ...rest] : activities;
  }, [activities, pinnedId]);

  const handleUploadOrEditSuccess = async () => {
    await fetchFeed(selectedTag);
    setUploadOpen(false);
    showToast("Activity uploaded!", "success");
  };

  if (!circle) return <div className="p-8">Loading circle…</div>;

  const container = "max-w-7xl mx-auto px-6 md:px-10 xl:px-16";
  const logo = circleLogoUrl(circle);
  const locText = circleLocation(circle);
  const website = circleWebsite(circle);
  const flag = codeToFlag(circleCountryCode(circle));

  return (
    <div className="app-shell min-h-screen">
      <header className="app-header">
        <AppHeader user={me || undefined} />
      </header>

      {/* Slim Header */}
      <div className="w-full bg-blue-50 pt-[calc(64px+var(--safe-top))]">
        <div className={`${container} md:mt-4 pb-4`}>
          <CircleSlimBanner
            circle={circle}
            isAdmin={isAdmin}
            logoBuster={logoBuster}
            locText={locText}
            website={website}
            flag={flag}
            openEditAllModal={openEditAllModal}
            openLogoPicker={openLogoPicker}
            logoFileInputRef={logoFileInputRef}
            onLogoFileChosen={onLogoFileChosen}
            onDeleteLogo={deleteLogo}
            circleLogoUrl={circleLogoUrl}
          />
        </div>
      </div>

      {/* CONTENT */}
      <main className={`app-content w-full ${container} pt-0 pb-8`}>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_minmax(0,2fr)_1fr] gap-6 items-start">
          {/* LEFT rail */}
          {!(onIOS || isMobile) && (
            <aside className="hidden md:block sticky top-0">
              <div className="overflow-auto overscroll-contain no-scrollbar space-y-4" style={{ maxHeight: RAIL_MAX_HEIGHT }}>
                {/* Desktop: Upcoming Event card */}
                <UpcomingEventCard
                  event={upcomingEvent}
                  eventsCount={allUpcomingEvents.length}
                  pastEventsCount={Math.max(0, (allEvents?.length || 0) - allUpcomingEvents.length)}
                  onManage={openEventsList}
                  onCreate={
                    isAdmin
                      ? () => (mobileView ? navigate(`/circles/${id}/events/new`) : setShowCreateEvent(true))
                      : undefined
                  }
                  onViewAll={openEventsList}
                  onShowRsvp={() => setRsvpEventId(upcomingEvent?.id)}
                  onSeeEvent={openEventsListForEvent}
                  meId={meId}
                  me={me}
                  onRefresh={async () => {
                    await fetchAllEvents();
                  }}
                  rsvp={rsvp}
                  getAuthHeaders={getAuthHeaders}
                />
                <PlannerQuickPanel
                  keySuffix="somepage"
                  monthLabel={monthLabel}
                  onMonthly={() => setShowMonthly(true)}
                  onWeekly={() => setShowWeekly(true)}
                  onWeekPlan={() => setShowWeekPlan(true)}
                  onGoals={() => setGoalsByMonth(true)}
                  onReminders={() => setShowReminders(true)}
                  streakDays={streakDays}
                  activitiesByDay={myActivitiesByDay}
                />
              </div>
            </aside>
          )}

          {/* MIDDLE */}
          <div className="md:col-span-1 space-y-4">
            {/* Mobile status + actions */}
            <div className="md:hidden bg-white rounded-2xl p-5">
              <div className="flex flex-wrap items-start gap-3">
                <span
                  className={[
                    "inline-flex items-center gap-1 font-medium px-2 py-0.5 rounded-full text-xs",
                    circle?.is_private ? "bg-blue-100  text-blue-800" : "bg-gray-100 text-gray-700",
                  ].join(" ")}
                  title={circle?.is_private ? "This circle is private" : "This circle is public"}
                >
                  {circle?.is_private ? <LockSimple size={14} /> : <LockSimpleOpen size={14} />}
                  {circle?.is_private ? "Private" : "Public"}
                </span>

                {isJoined ? (
                  <span className="text-blue-600 rounded-full font-medium text-xs inline-flex items-center gap-2">
                    <CheckCircle size={20} weight="duotone" className="-mr-1"/>
                    Joined
                    {isAdmin && (
                      <span className="inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium bg-blue-200 text-blue-900">
                        Admin
                      </span>
                    )}
                  </span>
                ) : null}
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                {isAdmin && isJoined && (
                  <button
                    type="button"
                    onClick={() =>
                      mobileView
                        ? navigate(`/circles/${id}/admin`)
                        : setShowAdminModal(true)
                    }
                    className={`${pillBtn} bg-blue-600 text-white text-sm rounded-full !px-2.5 !py-1 !h-7`}
                    style={{ lineHeight: 1 }}
                    title="Manage members & admins"
                  >
                    Manage
                  </button>
                )}

                <button
                  onClick={invite}
                  className={`${pillBtn} bg-blue-600 text-white rounded-full text-sm !px-2.5 !py-1 !h-7`}
                  style={{ lineHeight: 1 }}
                >
                  Invite
                </button>

                {isJoined ? (
                  <button
                    onClick={leave}
                    className={`${pillBtn} bg-blue-800 text-white rounded-full text-sm !px-2.5 !py-1 !h-7`}
                    style={{ lineHeight: 1 }}
                  >
                    Leave
                  </button>
                ) : (
                  <button
                    onClick={join}
                    className={`${pillBtn} bg-blue-800 text-white rounded-full text-sm !px-2.5 !py-1 !h-7`}
                    style={{ lineHeight: 1 }}
                  >
                    Join
                  </button>
                )}
              </div>
            </div>

            {/* Mobile: Upcoming Event card */}
            <div className="md:hidden ">
              <UpcomingEventCard
                event={upcomingEvent}
                eventsCount={allUpcomingEvents.length}
                pastEventsCount={Math.max(0, (allEvents?.length || 0) - allUpcomingEvents.length)}
                onManage={openEventsList}
                onCreate={
                  isAdmin
                    ? () => (mobileView ? navigate(`/circles/${id}/events/new`) : setShowCreateEvent(true))
                    : undefined
                }
                onViewAll={openEventsList}
                onShowRsvp={() => setRsvpEventId(upcomingEvent?.id)}
                onSeeEvent={openEventsListForEvent}
                meId={meId}
                me={me}
                onRefresh={async () => {
                  await fetchAllEvents();
                }}
                rsvp={rsvp}
                getAuthHeaders={getAuthHeaders}
              />
            </div>

            {/* Members (mobile) */}
            <div className="md:hidden p-5 pt-3 pb-1 bg-white rounded-2xl">
              <h3 className="text-lg font-semibold mb-3">Members</h3>
              <div className="flex -space-x-3">
                {firstFour.map((m) => (
                  <img
                    key={m.id}
                    src={
                      m?.profile_pic_url ||
                      DEFAULT_PROFILE_IMAGE
                    }
                    alt={m.name || `User #${m.id}`}
                    className="w-9 h-9 rounded-full object-cover border-2 border-white"
                  />
                ))}
              </div>
              <button className="mt-0 text-sm text-gray-900" onClick={() => setShowMembers(true)}>
                {members.length} member{members.length === 1 ? "" : "s"}
              </button>
            </div>

            {/* Mobile Upload */}
            {isJoined && (
              <div className="md:hidden mb-4 pointer-events-auto">
                <button
                  type="button"
                  onClick={handleUploadClick}
                  className="w-full rounded-full mb-4 px-4 py-3 bg-blue-600 text-white font-semibold hover:bg-blue-700 transition"
                >
                  Upload Activity
                </button>
              </div>
            )}

            {/* Announcements banner (always shown) */}
            <AnnouncementsBanner
              announcement={latestAnnouncement}
              isAdmin={isAdmin}
              onCreate={
                isAdmin
                  ? () => (mobileView ? navigate(`/circles/${id}/announcements/new`) : setShowCreateAnnouncement(true))
                  : undefined
              }
              onViewAll={() => (mobileView ? navigate(`/circles/${id}/announcements`) : setShowAnnouncementsList(true))}
            />

            {selectedTag && (
              <div className="flex items-center ml-3 gap-2">
                <span className="text-sm text-gray-600">Filtering by tag:</span>
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">#{selectedTag}</span>
                <button onClick={clearTag} className="text-xs text-gray-500 underline">
                  clear
                </button>
              </div>
            )}

            {/* Feed */}
            {sortedActivities.map((activity, index) => {
              const isPinned = pinnedId === activity.id;
              const isOwner = (activity?.user?.id || activity.user_id) === meId;

              return (
                <div key={activity.id} className="relative" style={index === 0 ? { marginTop: '1.5rem' } : undefined}>
                  {isPinned && (
                    <span className="absolute font-medium -top-2 left-3 z-10 text-blue-800 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-sky-200 text-black shadow-[0_2px_0px_rgba(37,99,235,0.96)]">
                      <PushPin size={14} weight="fill"/>
                      Pinned
                    </span>
                  )}

                  {(isAdmin || isOwner) && (
                    <div className="absolute right-3 -top-3 z-10 flex items-center gap-2">
                      {isAdmin &&
                        (isPinned ? (
                          <button
                            onClick={unpinActivity}
                            className={`${pillBtn} text-[12px] font-medium rounded-full bg-gray-100 hover:ring-2 hover:ring-gray-300 text-gray-700 !px-2 !py-1 !h-6`}
                            style={{ lineHeight: 1 }}
                            title="Unpin from circle"
                          >
                            Unpin
                          </button>
                        ) : (
                          <button
                            onClick={() => pinActivity(activity.id)}
                            className={`${pillBtn} text-[12px] rounded-full font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 inline-flex items-center gap-1 !px-2 !py-1 !h-6`}
                            style={{ lineHeight: 1 }}
                            title="Pin to top of circle"
                          >
                            <PushPin size={12} />
                            Pin
                          </button>
                        ))}

                      {(isOwner || isAdmin) && (
                        <button
                          onClick={() => setConfirmingDeleteId(activity.id)}
                          className={`${pillBtn} text-[12px] rounded-full bg-red-500 text-white hover:ring-2 hover:ring-red-300 inline-flex items-center gap-0.5 font-medium !px-2 !py-1 !h-6`}
                          style={{ lineHeight: 1 }}
                          title="Delete post"
                        >
                          <TrashSimple size={12} />
                          Delete
                        </button>
                      )}
                    </div>
                  )}
                  <ActivityCard
                    activity={normalizeActivity(activity)}
                    currentUser={me || undefined}
                    onTagClick={onTagClick}
                  />
                </div>
              );
            })}
          </div>

          {/* RIGHT rail */}
          <aside className="hidden md:block sticky top-0">
            <div className="overflow-auto overscroll-contain pr-1 space-y-4" style={{ maxHeight: RAIL_MAX_HEIGHT }}>

              {/* Status */}
              <div className="bg-white rounded-2xl p-5">
                <div className="flex flex-wrap items-start gap-3">
                  <span
                    className={[
                      "inline-flex items-center gap-1 px-2 py-0.5 font-medium rounded-full text-xs",
                      circle?.is_private ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-700",
                    ].join(" ")}
                    title={circle?.is_private ? "This circle is private" : "This circle is public"}
                  >
                    {circle?.is_private ? <LockSimple size={14} /> : <LockSimpleOpen size={14} />}
                    {circle?.is_private ? "Private" : "Public"}
                  </span>

                  {isJoined ? (
                    <span className="text-blue-600 font-medium text-xs inline-flex items-center gap-2">
                      <CheckCircle size={20} weight="duotone" className="-mr-1"/>
                      Joined
                      {isAdmin && (
                        <span className="inline-flex items-center text-xs px-2 py-0.5 font-medium rounded-full bg-blue-200 text-blue-900">
                          Admin
                        </span>
                      )}
                    </span>
                  ) : null}
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {isAdmin && isJoined && (
                    <button
                      type="button"
                      onClick={() => setShowAdminModal(true)}
                      className={`${pillBtn} bg-blue-600 text-white text-sm rounded-full hover:ring-2 hover:ring-blue-400 !px-2.5 !py-1 !h-7 flex items-center justify-center`}
                      style={{ lineHeight: 1 }}
                      title="Manage members & admins"
                    >
                      Manage
                    </button>
                  )}

                  <button
                    onClick={invite}
                    className={`${pillBtn} bg-blue-600 text-white rounded-full text-sm hover:ring-2 hover:ring-blue-400 !px-2.5 !py-1 !h-7`}
                    style={{ lineHeight: 1 }}
                  >
                    Invite
                  </button>

                  {isJoined ? (
                    <button
                      onClick={leave}
                      className={`${pillBtn} bg-blue-800 text-white rounded-full text-sm hover:ring-2 hover:ring-red-700 hover:bg-red-500 !px-2.5 !py-1 !h-7`}
                      style={{ lineHeight: 1 }}
                    >
                      Leave
                    </button>
                  ) : (
                    <button
                      onClick={join}
                      className={`${pillBtn} bg-blue-800 text-white rounded-full font-medium text-sm hover:bg-blue-400 !px-2.5 !py-1 !h-7`}
                      style={{ lineHeight: 1 }}
                    >
                      Join
                    </button>
                  )}
                </div>

              </div>

              {/* Members */}
              <div className="bg-white rounded-2xl pt-3 pb-3 p-5 ">
                <h3 className="text-lg font-semibold mb-2">Members</h3>
                <div className="flex -space-x-3">
                  {firstFour.map((m) => (
                    <img
                      key={m.id}
                      src={
                        m?.profile_pic_url ||
                        DEFAULT_PROFILE_IMAGE
                      }
                      alt={m.name || `User #${m.id}`}
                      className="w-9 h-9 rounded-full object-cover border-2 border-white"
                    />
                  ))}
                </div>
                <button
                  className="mt-2 ml-1 text-sm text-gray-900 hover:text-blue-600 hover:font-medium"
                  onClick={() => setShowMembers(true)}
                >
                  {members.length} member{members.length === 1 ? "" : "s"}
                </button>
              </div>

              {/* Upload */}
              {isJoined && (
                <div className="mb-4 pointer-events-auto">
                  <button
                    type="button"
                    onClick={handleUploadClick}
                    className="w-full rounded-3xl px-3 py-2 bg-blue-600 text-white font-semibold hover:bg-blue-700 transition"
                  >
                    Upload Activity
                  </button>
                </div>
              )}
            </div>
          </aside>
        </div>
        <div className="app-footer-pad" />
      </main>

      <CircleToast
        toast={toast}
        onClose={() => setToast(null)}
        onCopyAgain={async () => {
          const ok = await copyText(window.location.href);
          if (ok) {
            showToast("Invite link copied again", "success", 2000, true);
          } else {
            showToast("Copy failed - try again", "error");
          }
        }}
      />

      {/* Edit Circle Details modal */}
      <EditCircleDetailsModal
        open={isAdmin && showEditMeta}
        onClose={closeEditAllModal}
        onSave={saveAllFields}
        draftNameAll={draftNameAll}
        setDraftNameAll={setDraftNameAll}
        draftCityAll={draftCityAll}
        setDraftCityAll={setDraftCityAll}
        countryOptions={COUNTRY_OPTIONS}
        draftCountryCodeAll={draftCountryCodeAll}
        setDraftCountryCodeAll={setDraftCountryCodeAll}
        draftWebsiteAll={draftWebsiteAll}
        setDraftWebsiteAll={setDraftWebsiteAll}
        draftDescriptionAll={draftDescriptionAll}
        setDraftDescriptionAll={setDraftDescriptionAll}
        draftLocationLabel={draftLocationLabel}
        codeToFlag={codeToFlag}
      />

      {/* Desktop Event Create Modal (admin only) */}
      {isAdmin && showCreateEvent && !(onIOS || isMobile) && (
        <EventCreateModal
          groupId={Number(id)}
          open
          onClose={() => setShowCreateEvent(false)}
          onCreated={async () => {
            // refresh both single "next" and list
            await fetchAllEvents();
            setShowCreateEvent(false);
          }}
        />
      )}

      {/* Admin Modal */}
      <CircleAdminModal
        open={showAdminModal}
        onClose={async () => {
          setShowAdminModal(false);
          try {
            const headers = await getAuthHeaders();
            const [memRes, adminRes] = await Promise.all([
              api.get(`/groups/${id}/members`, { headers }),
              api.get(`/groups/${id}/admins`, { headers }).catch(() => ({ data: [] })),
            ]);
            const hydratedMembers = await hydrateMembers(
              memRes.data || [],
              headers
            );

            setMembers(hydratedMembers);
            setAdmins(Array.isArray(adminRes?.data) ? adminRes.data : []);
            const meStr = String(me?.id || localStorage.getItem("user_id"));
            setIsAdmin((adminRes?.data || []).some((a) => String(a.id) === meStr));
          } catch {}
        }}
        groupId={Number(id)}
        meId={me?.id || Number(localStorage.getItem("user_id"))}
      />

      {/* Members & Calendars */}
      <MembersModal
        open={showMembers}
        onClose={() => setShowMembers(false)}
        members={members}
        meId={meId}
        groupId={Number(id)}
      />
      <MonthlyCalendarModal open={showMonthly} onClose={() => setShowMonthly(false)} activitiesByDay={myActivitiesByDay} currentUser={me} streakDays={streakDays} />
      <WeeklyCalendarModal open={showWeekly} onClose={() => setShowWeekly(false)} activityDates={myActivityDates} />
      <WeeklyPlanModal open={showWeekPlan} onClose={() => setShowWeekPlan(false)} keySuffix={`circle:${id}`} />
      <GoalsByMonth open={showGoalsByMonth} onClose={() => setGoalsByMonth(false)} keySuffix="home" />
      <RemindersModal open={showReminders} onClose={() => setShowReminders(false)} storageKey={`circle:${id}:reminders`} />
      <RsvpListModal open={!!rsvpEventId} onClose={() => setRsvpEventId(null)} eventId={rsvpEventId} />
      <AnnouncementsListModal
        open={showAnnouncementsList && !(onIOS || isMobile)}
        onClose={() => setShowAnnouncementsList(false)}
        announcements={allAnnouncements}
        isAdmin={isAdmin}
        groupId={id}
        onCreated={fetchAnnouncements}
        onEdit={editAnnouncement}
        onDelete={deleteAnnouncement}
      />

      {isAdmin && showCreateAnnouncement && !(onIOS || isMobile) && (
        <AnnouncementCreateModal
          groupId={Number(id)}
          open
          onClose={() => setShowCreateAnnouncement(false)}
          onCreated={async () => {
            await fetchAnnouncements();
            setShowCreateAnnouncement(false);
          }}
        />
      )}

      {/* Logo Cropper */}
      <CropperModal
        open={showLogoCropper}
        image={logoCropSrc}
        crop={logoCrop}
        zoom={logoZoom}
        aspect={logoAspect}
        cropPixels={logoCropPixels}
        setCrop={setLogoCrop}
        setZoom={setLogoZoom}
        onCropComplete={onLogoCropComplete}
        onClose={skipLogoCrop}
        onSkip={skipLogoCrop}
        onApply={applyCroppedLogo}
        title="Crop"
        skipLabel="Skip"
        applyLabel="Save"
        cropShape="round"
      />

      {/* Desktop Upload Modal */}
      {!mobileView && uploadOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setUploadOpen(false)}
          />
          <div className="relative z-10 w-full max-w-xl rounded-2xl bg-slate-50 shadow-xl overflow-hidden">
            <div className="flex items-center justify-between px-5 pt-4 pb-2">
              <h3 className="font-black tracking-[-0.01em] text-blue-600 text-[18px]">
                Upload Activity
              </h3>
              <button
                onClick={() => setUploadOpen(false)}
                className="grid h-8 w-8 place-items-center text-gray-500"
                aria-label="Close"
              >
                <X size={18} weight="bold" />
              </button>
            </div>
            <div className="max-h-[80dvh] overflow-y-auto">
              <ActivityUpload
                circleId={circle?.id ?? null}
                circleName={circle?.name ?? null}
                redirectTo={
                  circle?.id
                    ? { type: "circle", id: circle.id }
                    : null
                }
                canGoBack={false}
                fetchFeed={handleUploadOrEditSuccess}
                onClose={() => setUploadOpen(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* View All Events Modal */}
      <EventsListModal
        open={showAllEvents && !mobileView}
        onClose={() => {
          setShowAllEvents(false);
          setHighlightEventId(null);
        }}
        events={allEvents}
        isAdmin={isAdmin}
        highlightEventId={highlightEventId}
        onChanged={async () => {
          await fetchAllEvents();
        }}
      />

      {/* Delete confirmation modal */}
      {confirmingDeleteId && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setConfirmingDeleteId(null)}
            aria-hidden="true"
          />

          <div className="relative w-full max-w-sm md:max-w-md bg-white rounded-full shadow-[0_8px_0px_rgba(37,99,235,0.96)] py-6">
            <div className="flex items-start justify-center gap-4">
              <div>
                <p className="text-md font-medium leading-tight text-black">
                  Delete this activity?
                </p>
                <p className="text-sm text-black">This can't be undone</p>
              </div>
            </div>

            <div className="mt-3 flex items-center justify-center gap-2">
              <button
                type="button"
                onClick={() => setConfirmingDeleteId(null)}
                disabled={deletingActivityId === confirmingDeleteId}
                className="border border-blue-700 hover:ring-2 hover:ring-blue-700 text-sm rounded-full px-3 py-1.5 disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={() => deleteActivity(confirmingDeleteId)}
                disabled={deletingActivityId === confirmingDeleteId}
                className="text-sm text-white rounded-full px-3 py-2 bg-red-600 hover:ring-2 hover:ring-red-300 disabled:opacity-50"
              >
                {deletingActivityId === confirmingDeleteId ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Circle;
